import { useCallback, useMemo, useRef } from 'react';
import { Button, Skeleton, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import ThreadHeader from './ThreadHeader.jsx';
import ThreadCommentList from './ThreadCommentList.jsx';
import ThreadComposer from './ThreadComposer.jsx';
import { useWorkQueueContext } from '../../context/useWorkQueueContext.js';
import { collectThreadParticipants } from '../../utils/collectThreadParticipants.js';
import { buildQuestionThreadSeed } from '../../utils/buildQuestionThreadSeed.js';
import { threadClosedToast, threadReopenedToast } from '../../utils/threadActionToast.js';
import { isDecisionSkipped } from '../../utils/decisionSkipped.js';
import { hitlActionErrorToast } from '../../utils/hitlActionToast.js';
import { EVENT_TYPE } from '../../constants/eventTypes.js';

function ThreadTab({ thread, eventId, event = null, layout = 'stack', loading = false, error = null }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { postThreadComment, completeQuestion, closeEventThread, reopenEventThread } =
    useWorkQueueContext();
  // Mirror the Question card into the thread when nothing is persisted yet (aims-htl).
  const comments = useMemo(() => {
    if (thread.comments?.length) return thread.comments;
    if (event?.type === EVENT_TYPE.QUESTION) {
      return buildQuestionThreadSeed(event).comments ?? [];
    }
    return [];
  }, [event, thread.comments]);
  const status = thread.status ?? 'open';
  const isPanel = layout === 'panel';
  const isClosed = status === 'closed';
  const participants = collectThreadParticipants(comments);
  const submittingRef = useRef(false);
  const composerPlaceholder =
    event?.type === EVENT_TYPE.QUESTION
      ? t('thread.questionPlaceholder')
      : t('thread.placeholder');

  const handleCloseThread = useCallback(async () => {
    if (isClosed) return;
    try {
      await closeEventThread?.(eventId);
      toast.success(threadClosedToast(t));
    } catch (err) {
      toast.error({
        title: t('thread.postErrorTitle'),
        message: err?.message ?? t('thread.postErrorMessage'),
      });
    }
  }, [closeEventThread, eventId, isClosed, t, toast]);

  const handleReopenThread = useCallback(async () => {
    if (!isClosed) return;
    try {
      await reopenEventThread?.(eventId);
      toast.success(threadReopenedToast(t));
    } catch (err) {
      toast.error({
        title: t('thread.postErrorTitle'),
        message: err?.message ?? t('thread.postErrorMessage'),
      });
    }
  }, [eventId, isClosed, reopenEventThread, t, toast]);

  const handlePost = useCallback(
    async ({ body, mentions }) => {
      if (submittingRef.current || isClosed) return;
      submittingRef.current = true;
      try {
        // Thread Post is always local (threadActions mock) — never hitl/resolve.
        // Answering a direct HITL question via Platform is a footer decision
        // (Decline → rejected); context/comments belong in the thread panel.
        await postThreadComment?.(eventId, { body, mentions });
      } catch (err) {
        toast.error({
          title: t('thread.postErrorTitle'),
          message: err?.message ?? t('thread.postErrorMessage'),
        });
      } finally {
        submittingRef.current = false;
      }
    },
    [eventId, isClosed, postThreadComment, t, toast]
  );

  const handleMarkAnswered = useCallback(
    async (questionEventId) => {
      try {
        await completeQuestion?.(questionEventId, { outcome: 'answered' });
        toast.success({
          title: t('question.markedAnsweredTitle'),
          message: t('question.markedAnsweredMessage'),
        });
      } catch (err) {
        if (isDecisionSkipped(err)) return;
        const { title, message } = hitlActionErrorToast(t, err, 'question');
        toast.error({ title, message });
      }
    },
    [completeQuestion, t, toast]
  );

  let listContent;
  if (loading) {
    listContent = (
      <div className="flex flex-col gap-4" aria-busy="true" aria-live="polite">
        {['sk-1', 'sk-2'].map((key) => (
          <div key={key} className="flex gap-3">
            <Skeleton width={36} height={36} borderRadius={9999} />
            <div className="min-w-0 flex-1">
              <Skeleton height={12} width="33%" />
              <div className="mt-2">
                <Skeleton height={12} width="80%" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (error) {
    listContent = (
      <p className="text-sm text-red-500 dark:text-red-400">
        {t('thread.error', { defaultValue: error })}
      </p>
    );
  } else {
    listContent = <ThreadCommentList comments={comments} onMarkAnswered={handleMarkAnswered} />;
  }

  const footer = isClosed ? (
    <div className="flex flex-col items-center gap-2 py-2 text-center">
      <p className="text-xs text-content-faint">{t('thread.closedNotice')}</p>
      <Button variant="ghost" size="sm" className="text-[#2173FF] dark:text-blue-400" onClick={handleReopenThread}>
        {t('thread.reopen')}
      </Button>
    </div>
  ) : (
    <ThreadComposer
      onPost={handlePost}
      placeholder={composerPlaceholder}
    />
  );

  const header = (
    <ThreadHeader
      participants={participants}
      commentCount={comments.length}
      status={status}
      onClose={handleCloseThread}
    />
  );

  if (!isPanel) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-5">
        {header}
        {listContent}
        <div className="border-t border-edge pt-4">{footer}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 scrollbar-aimsos-auto-sm">
        <div className="flex w-full min-w-0 flex-col gap-5">
          {header}
          {listContent}
        </div>
      </div>
      <div className="shrink-0 border-t border-edge px-4 py-3">{footer}</div>
    </div>
  );
}

export default ThreadTab;
